import { Box, Stack, SimpleGrid, Text, Group, Badge } from '@mantine/core'
import {
  IconTrendingUp,
  IconCheckbox,
  IconAlertTriangle,
} from '@tabler/icons-react'
import { useDashboardData } from '@/hooks/useDashboardData'
import { SmallBox, WidgetCard, CardHeader } from '@/components/ui'
import { corDeStatus } from '@/utils/metrica'

/**
 * Business Tab Component (TASK-1224)
 */
export function BusinessTab() {
  const { kpis, budget } = useDashboardData('po')

  const tickets = kpis.data?.tickets
  const budgetData = budget.data

  // Ausencia de QUALQUER um dos dois lados torna o percentual desconhecido.
  const percentualDoOrcamento =
    budgetData?.current_cost_usd == null || budgetData?.budget_limit_usd == null
      ? null
      : (Number(budgetData.current_cost_usd) / Math.max(Number(budgetData.budget_limit_usd), 1)) * 100

  return (
    <Stack gap="xl">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <SmallBox
          value={tickets?.open ?? '—'}
          label="Chamados Abertos"
          icon={<IconCheckbox size={40} />}
          color="warning"
          linkLabel="Ver chamados"
          linkTo="/chamados"
          loading={kpis.isLoading}
          info="Quantidade de chamados de suporte ainda abertos. Valores elevados podem indicar gargalo no atendimento ou incidentes em andamento."
        />
        <SmallBox
          value={budgetData?.current_cost_usd != null ? `$${Number(budgetData.current_cost_usd).toFixed(2)}` : '—'}
          label="Custo Mensal"
          icon={<IconTrendingUp size={40} />}
          color="danger"
          linkLabel="Ver FinOps"
          linkTo="/finops"
          loading={kpis.isLoading}
          info="Gasto acumulado na AWS no mês corrente. Fique atento quando se aproximar ou ultrapassar o orçamento definido."
        />
        <SmallBox
          value={budgetData?.budget_limit_usd != null ? `$${Number(budgetData.budget_limit_usd).toFixed(2)}` : '—'}
          label="Orçamento"
          icon={<IconAlertTriangle size={40} />}
          color="info"
          loading={kpis.isLoading}
          info="Limite de orçamento mensal configurado para este tenant. Compare com o Custo Mensal para avaliar o uso do budget."
        />
        <SmallBox
          value="24h"
          label="MTTR Médio"
          icon={<IconAlertTriangle size={40} />}
          color="success"
          loading={kpis.isLoading}
          info="Tempo Médio de Recuperação (Mean Time to Recover): tempo médio para resolver incidentes. Preocupe-se quando ultrapassar 48h."
        />
      </SimpleGrid>

      {budgetData && (
        <WidgetCard accent="info" p={0}>
          <CardHeader title="Orçamento vs Gasto" />
          <Box p="md">
            <Group justify="space-between" mb="md">
              <Box>
                <Text size="sm" c="dimmed">Gasto Atual</Text>
                <Text size="xl" fw={700}>${Number(budgetData.current_cost_usd ?? 0).toFixed(2)}</Text>
              </Box>
              <Box ta="right">
                <Text size="sm" c="dimmed">Limite</Text>
                <Text size="xl" fw={700}>${Number(budgetData.budget_limit_usd ?? 0).toFixed(2)}</Text>
              </Box>
            </Group>
            <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Text size="sm" fw={500}>Uso</Text>
              <Text size="sm" fw={500}>
                {Math.round((Number(budgetData.current_cost_usd ?? 0) / Math.max(Number(budgetData.budget_limit_usd ?? 1), 1)) * 100)}%
              </Text>
            </Box>
            <Box
              style={{
                width: '100%',
                height: '12px',
                backgroundColor: 'var(--ai-border-light)',
                borderRadius: '6px',
                overflow: 'hidden',
              }}
            >
              <Box
                style={{
                  width: `${Math.min(percentualDoOrcamento ?? 0, 100)}%`,
                  height: '100%',
                  // DOIS defaults independentes erravam para o verde aqui: custo ausente virava
                  // 0, e limite ausente virava Infinity — e nada e maior que infinito, entao a
                  // barra NUNCA ficava vermelha quando faltava o limite. Sem dado, sem cor.
                  backgroundColor: corDeStatus(percentualDoOrcamento, [
                    { ate: 100, cor: '#2f9e44' },
                    { ate: Infinity, cor: '#e03131' },
                  ]) ?? '#adb5bd',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
          </Box>
        </WidgetCard>
      )}

      <WidgetCard accent="success" p={0}>
        <CardHeader title="SLA Compliance" />
        <Box p="md">
          <Text size="sm" ta="center">85% — Meta: 95%</Text>
        </Box>
      </WidgetCard>
    </Stack>
  )
}
